import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { tw } from 'utils/classnames'

/**
 * Unofficial variants while colors are in-progress by the design system team
 */
const customVariants = {
  'primary-green':
    'text-cf-theme-green-600  hover:text-cf-theme-green-700 active:text-cf-theme-green-900',
}

const linkVariants = cva(
  'font-medium inline-flex items-center whitespace-nowrap rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 underline-offset-4 hover:underline [&_svg]:pointer-events-none [&_svg]:shrink-0 focus-visible:outline-cf-blue-700 active:outline-cf-blue-300 ',
  {
    variants: {
      variant: {
        primary: 'text-cf-blue-700  active:text-cf-blue-800 focus-visible:outline-cf-blue-700',

        subtle:
          'text-cf-grey-600  hover:text-cf-grey-1000 active:text-cf-grey-800 focus-visible:outline-cf-grey-600',
        'primary-inverse':
          'text-cf-grey-0 hover:text-cf-transparent-grey-0-88 active:text-cf-transparent-grey-0-72 focus-visible:outline-cf-blue-400 focus-visible:text-cf-grey-0',
        'subtle-inverse':
          'text-cf-transparent-grey-0-72  active:text-cf-transparent-grey-0-64 focus-visible:outline-cf-blue-400 focus-visible:text-cf-transparent-grey-0-72  focus-visible:outline-cf-blue-400',
        ...customVariants,
      },
      size: {
        regular: 'text-14/5 gap-1.5 [&_svg]:w-4 [&_svg]:h-4',
        small: 'text-12/4 gap-1 [&_svg]:w-3.5 [&_svg]:h-3.5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'regular',
    },
  },
)

export interface LinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement>, VariantProps<typeof linkVariants> {
  asChild?: boolean
  /**
   * If true, the link will be inline and will have an underline
   */
  inline?: boolean
  icon?: React.ReactNode
}

const Link = ({
  ref,
  className,
  variant,
  size,
  asChild = false,
  children,
  inline = false,
  icon,
  ...props
}: LinkProps & {
  ref?: React.RefObject<HTMLAnchorElement | null>
}) => {
  const underlineClass = inline ? 'underline inline' : ''
  const Comp = asChild ? Slot : 'a'

  const linkClassName = tw(linkVariants({ variant, size, className }), underlineClass)

  if (asChild) {
    // oxlint-disable-next-line no-else-return
    return (
      <Slot className={linkClassName} ref={ref} {...props}>
        {children}
      </Slot>
    )
  } else {
    return (
      <Comp className={linkClassName} ref={ref} {...props}>
        {children}
        {icon}
      </Comp>
    )
  }
}
Link.displayName = 'Link'

export { Link, linkVariants }
